// src/components/coupon/BrandSelectModal.tsx
import * as React from "react";
import useSWR from "swr";
import { Button, Input, Modal, Pagination, Space, Spin, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getBrandListApi } from "src/api/brand/brandApi";

export type SelectedBrand = {
  id: number;
  name: string;
};

type Props = {
  open: boolean;
  selectedIds: number[];
  selectedItems?: SelectedBrand[];
  onClose: () => void;
  onConfirm: (selectedItems: SelectedBrand[]) => void;
};

export default function BrandSelectModal({ open, selectedIds, selectedItems, onClose, onConfirm }: Props) {
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [size] = React.useState(10);
  const [tempSelected, setTempSelected] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (open) {
      setTempSelected([...selectedIds]);
      setSearch("");
      setPage(1);
    }
  }, [open, selectedIds]);

  const { data, isLoading } = useSWR(
    open ? ["brand/list/modal", search, page, size] : null,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getBrandListApi({
        brandName: search.trim() || undefined,
        page: apiPage,
        size,
        order: "id,desc",
      });
    },
    { revalidateOnFocus: false }
  );

  const list = data?.data?.content ?? [];
  const totalElements = data?.data?.page?.totalElements ?? 0;

  const handleConfirm = () => {
    const result: SelectedBrand[] = tempSelected.map((id) => {
      const brand = list.find((item: any) => item.id === id);
      if (brand) {
        return {
          id: brand.id,
          name: brand.brandNameKo || brand.brandNameEn || `브랜드 ${brand.id}`,
        };
      }
      const prevSelected = selectedItems?.find((item) => item.id === id);
      if (prevSelected) return prevSelected;
      return { id, name: `브랜드 ${id}` };
    });
    onConfirm(result);
    onClose();
  };

  const columns: ColumnsType<any> = [
    { title: "ID", dataIndex: "id", width: 80 },
    {
      title: "브랜드명",
      dataIndex: "brandNameKo",
      render: (value, record) => (
        <div>
          <Typography.Text strong>{value || record.brandNameEn || "-"}</Typography.Text>
          <div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {record.brandNameEn || ""}
            </Typography.Text>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="브랜드 선택"
      width={900}
      footer={
        <Space>
          <Button onClick={onClose}>취소</Button>
          <Button type="primary" onClick={handleConfirm}>선택</Button>
        </Space>
      }
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="브랜드명 검색"
        />

        <Typography.Text type="secondary">
          선택된 브랜드: {tempSelected.length}개
        </Typography.Text>

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <Spin />
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={list}
            rowKey={(item) => item.id}
            pagination={false}
            scroll={{ x: 1000 }}
            rowSelection={{
              selectedRowKeys: tempSelected,
              onChange: (keys) => setTempSelected(keys.map(k => Number(k))),
            }}
            onRow={(record) => ({
              onClick: () => {
                setTempSelected((prev) =>
                  prev.includes(record.id)
                    ? prev.filter((id) => id !== record.id)
                    : [...prev, record.id]
                );
              },
            })}
          />
        )}

        <div style={{ display: "flex", justifyContent: "center" }}>
          <Pagination
            current={page}
            total={Math.max(1, totalElements)}
            pageSize={size}
            onChange={(nextPage) => setPage(nextPage)}
            showSizeChanger={false}
          />
        </div>
      </Space>
    </Modal>
  );
}
